import { useEffect, useLayoutEffect, useState } from 'react';
import { useSingleColumn } from '../../hooks/useMedia';
import { useT } from '../../i18n';
import { useUi } from '../../store/ui';
import { TOUR_STEPS, useTour, type TourStep } from '../../tour/store';
import controls from '../controls.module.css';
import styles from './Tour.module.css';

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PAD = 6;

function visible(el: Element): boolean {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/** The element a step points at, or null for the closing step. */
function targetFor(step: TourStep, single: boolean, column: number, exampleId: string | undefined): Element | null {
  const first = (selector: string) => Array.from(document.querySelectorAll(selector)).find(visible) ?? null;
  switch (step) {
    case 'columns':
      return single ? first('[data-tour="columnbar"]') : first('[data-col="0"] [data-tour="colhead"]');
    case 'bands':
      return first(`[data-col="${single ? column : 0}"] [data-tour="band"]`);
    case 'energy':
      return exampleId ? first(`[data-card="${exampleId}"] [data-tour="energy"]`) : null;
    case 'battery':
      return first('[data-tour="lens"]');
    case 'add':
      return first(`[data-col="${single ? column : 0}"] [data-tour="add"]`);
    default:
      return null;
  }
}

/**
 * A guided walk over the real board: a spotlight on one element per step and a short card
 * with what it does. The rest of the screen stays usable, so the user can try each thing.
 */
export function Tour() {
  const t = useT();
  const single = useSingleColumn();
  const active = useTour((s) => s.active);
  const index = useTour((s) => s.index);
  const exampleIds = useTour((s) => s.exampleIds);
  const next = useTour((s) => s.next);
  const back = useTour((s) => s.back);
  const finish = useTour((s) => s.finish);
  const column = useUi((s) => s.column);
  const jumpTo = useUi((s) => s.jumpTo);
  const [box, setBox] = useState<Box | null>(null);
  const step = TOUR_STEPS[index] ?? 'finish';

  // The example card for the energy step lives in the first column; one column at a time has to show it.
  useEffect(() => {
    if (active && single && step === 'energy' && column !== 0) jumpTo(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, single, step]);

  useLayoutEffect(() => {
    if (!active) return;
    const measure = () => {
      const el = targetFor(step, single, column, exampleIds[0]);
      if (!el) {
        setBox(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setBox((prev) => {
        const nextBox = { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 };
        return prev && Math.abs(prev.top - nextBox.top) < 1 && Math.abs(prev.left - nextBox.left) < 1 && prev.width === nextBox.width && prev.height === nextBox.height ? prev : nextBox;
      });
    };
    measure();
    const timer = setInterval(measure, 250);
    window.addEventListener('resize', measure);
    return () => {
      clearInterval(timer);
      window.removeEventListener('resize', measure);
    };
  }, [active, step, single, column, exampleIds]);

  if (!active) return null;
  const last = index === TOUR_STEPS.length - 1;
  const vh = typeof window === 'undefined' ? 800 : window.innerHeight;
  const vw = typeof window === 'undefined' ? 400 : window.innerWidth;
  // The card goes below the target when there is room, above it otherwise; with one column at a time it
  // hugs the top or the bottom edge, whichever is away from the target.
  let cardStyle: React.CSSProperties;
  if (!box) cardStyle = single ? { left: 16, right: 16, bottom: 'calc(110px + var(--sab))' } : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
  else if (single) cardStyle = box.top + box.height / 2 > vh / 2 ? { left: 16, right: 16, top: 'calc(72px + var(--sat))' } : { left: 16, right: 16, bottom: 'calc(110px + var(--sab))' };
  else {
    const below = box.top + box.height + 220 < vh;
    const left = Math.max(16, Math.min(vw - 376, box.left));
    cardStyle = below ? { left, top: box.top + box.height + 12 } : { left, bottom: vh - box.top + 12 };
  }

  return (
    <div className={styles.layer} aria-live="polite">
      {box ? <div className={styles.spotlight} style={{ top: box.top, left: box.left, width: box.width, height: box.height }} /> : <div className={styles.dim} />}
      <div className={styles.card} role="dialog" aria-label={t.t('tour.title')} style={cardStyle}>
        <div className={styles.progress}>{t.t('tour.progress', { n: index + 1, total: TOUR_STEPS.length })}</div>
        <p className={styles.text}>{t.t(`tour.step.${step}`)}</p>
        {last ? (
          <div className={controls.footer}>
            <button type="button" className={`${controls.btn} ${controls.quiet}`} onClick={() => void finish(true)}>
              {t.t('tour.keep')}
            </button>
            <button type="button" className={`${controls.btn} ${controls.primary}`} onClick={() => void finish(false)}>
              {t.t('tour.remove')}
            </button>
          </div>
        ) : (
          <div className={controls.footer}>
            <button type="button" className={`${styles.skip} ${controls.footerLeft}`} onClick={() => void finish(false)}>
              {t.t('tour.skip')}
            </button>
            {index > 0 ? (
              <button type="button" className={`${controls.btn} ${controls.quiet}`} onClick={back}>
                {t.t('tour.back')}
              </button>
            ) : null}
            <button type="button" className={`${controls.btn} ${controls.primary}`} onClick={next}>
              {t.t('tour.next')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
